// ELS SÍMBOLS DE LA IMATGE RICA: com es dibuixen i com s'encerten amb el ratolí.
//
// Fora de React, amb un sol import (`tema.js`). Es prova sol: `npm run prova:imatge`.
//
// ── QUÈ FALLAVA ──────────────────────────────────────────────────────────────────────────────
//
// **1. El color era una fallada silenciosa.** Cada element es creava amb
// `tool.color.replace('text-', '#')`, o sigui `'text-primary-600'` → `'#primary-600'`, que **no
// és cap color CSS**. El `canvas` ignora en silenci un `strokeStyle` invàlid i **es queda el
// que hi havia**: el de la reixa, `#E2E0DA`. Per això tot sortia d'un gris pàl·lid.
//
// **2. Cinc dels vuit símbols no existien.** Només hi havia dibuix per a l'actor, la
// preocupació, l'estructura, el procés i el globus; el clima bo, el dolent i la fletxa queien al
// `default` i sortien com **un rectangle de 30×20**. La majoria d'icones eren quadrats.
//
// **3. Tot era massa prim i sense cos.** Traç de 2 px i cap farciment, en un dibuix que es mira
// en grup i de lluny.
//
// Ara cada símbol té **farciment clar i traç fort**, la mida és prou gran per reconèixer-se, i
// **el color surt de `tema.js`** —mai d'una classe de Tailwind, que és el que va portar aquí—.

import { SIMBOLS, LLENC } from './tema';

// El radi de la caixa d'un símbol. Es dibuixen centrats a (x, y): abans es dibuixaven des del
// cantó i cada símbol posava els seus desplaçaments a mà, de manera que la zona per clicar i el
// dibuix no coincidien.
export const RADI = 26;
export const GRUIX = 3;

export const TIPUS = ['actor', 'structure', 'process', 'concern',
                      'climate_good', 'climate_bad', 'bubble'];

const colorsDe = (tipus) => SIMBOLS[tipus] || SIMBOLS.actor;

// Encertar-lo amb el ratolí. Una caixa quadrada al voltant del centre, prou generosa perquè
// també hi entri l'etiqueta de sota.
export function dinsDe(element, x, y) {
  const dx = x - element.x, dy = y - element.y;
  return Math.abs(dx) <= RADI && dy >= -RADI && dy <= RADI + 16;
}

// El de més amunt de la pila que hi hagi sota el punter. **L'últim de la llista guanya**: és el
// que s'ha posat més tard i el que es veu a sobre.
export function aQuinElement(elements, x, y) {
  for (let i = elements.length - 1; i >= 0; i--) {
    if (dinsDe(elements[i], x, y)) return elements[i];
  }
  return null;
}

// ── EL DIBUIX ────────────────────────────────────────────────────────────────────────────────

function cara(ctx, x, y, contenta, traç) {
  ctx.beginPath();
  ctx.arc(x, y, RADI * 0.78, 0, Math.PI * 2);
  ctx.fill(); ctx.stroke();
  const r = RADI * 0.78;
  ctx.beginPath();
  ctx.arc(x - r * 0.36, y - r * 0.24, r * 0.12, 0, Math.PI * 2);
  ctx.arc(x + r * 0.36, y - r * 0.24, r * 0.12, 0, Math.PI * 2);
  ctx.fillStyle = traç;
  ctx.fill();
  ctx.beginPath();
  if (contenta) ctx.arc(x, y + r * 0.10, r * 0.48, 0.15 * Math.PI, 0.85 * Math.PI);
  else ctx.arc(x, y + r * 0.62, r * 0.48, 1.15 * Math.PI, 1.85 * Math.PI);
  ctx.stroke();
}

export function dibuixaSimbol(ctx, element, { triat = false } = {}) {
  const c = colorsDe(element.type);
  const x = element.x, y = element.y;

  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.fillStyle = c.farciment;
  ctx.strokeStyle = c.traç;
  ctx.lineWidth = triat ? GRUIX + 1.5 : GRUIX;

  switch (element.type) {
    case 'actor': {
      // Una persona: cap i espatlles.
      ctx.beginPath();
      ctx.arc(x, y - RADI * 0.38, RADI * 0.36, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y + RADI * 0.62, RADI * 0.66, Math.PI, 2 * Math.PI);
      ctx.fill(); ctx.stroke();
      break;
    }
    case 'structure': {
      // Una estructura: una casa. El que s'està i no es mou.
      const a = RADI * 0.86;
      ctx.beginPath();
      ctx.moveTo(x - a, y + a * 0.7);
      ctx.lineTo(x - a, y - a * 0.1);
      ctx.lineTo(x, y - a * 0.95);
      ctx.lineTo(x + a, y - a * 0.1);
      ctx.lineTo(x + a, y + a * 0.7);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      break;
    }
    case 'process': {
      // Un procés: una fletxa dins d'un cercle. El que passa.
      ctx.beginPath();
      ctx.arc(x, y, RADI * 0.8, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, RADI * 0.42, -0.6 * Math.PI, 0.9 * Math.PI);
      ctx.stroke();
      const p = RADI * 0.42;
      ctx.beginPath();
      ctx.moveTo(x + p * 0.15, y - p * 1.05);
      ctx.lineTo(x - p * 0.45, y - p * 0.85);
      ctx.lineTo(x + p * 0.10, y - p * 0.35);
      ctx.closePath();
      ctx.fillStyle = c.traç; ctx.fill();
      break;
    }
    case 'concern': {
      // Una preocupació: el triangle d'avís, amb l'exclamació.
      const a = RADI * 0.92;
      ctx.beginPath();
      ctx.moveTo(x, y - a);
      ctx.lineTo(x + a * 0.95, y + a * 0.72);
      ctx.lineTo(x - a * 0.95, y + a * 0.72);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, y - a * 0.34);
      ctx.lineTo(x, y + a * 0.16);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y + a * 0.42, GRUIX * 0.62, 0, Math.PI * 2);
      ctx.fillStyle = c.traç; ctx.fill();
      break;
    }
    case 'climate_good': cara(ctx, x, y, true, c.traç); break;
    case 'climate_bad': cara(ctx, x, y, false, c.traç); break;
    case 'bubble': {
      // El que algú diu o pensa.
      const a = RADI * 0.95, b = RADI * 0.66;
      ctx.beginPath();
      ctx.ellipse(x, y - RADI * 0.14, a, b, 0, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x - a * 0.30, y + b * 0.62);
      ctx.lineTo(x - a * 0.46, y + b * 1.42);
      ctx.lineTo(x - a * 0.02, y + b * 0.74);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      break;
    }
    default: {
      ctx.beginPath();
      ctx.arc(x, y, RADI * 0.7, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
    }
  }

  // L'anell de tria: fora del símbol, perquè no en tapi el dibuix.
  if (triat) {
    ctx.strokeStyle = LLENC.estoc.seleccio;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.strokeRect(x - RADI - 4, y - RADI - 4, (RADI + 4) * 2, (RADI + 4) * 2 + 14);
    ctx.setLineDash([]);
  }

  if (element.name) {
    ctx.fillStyle = LLENC.text;
    ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    // Vora blanca: l'etiqueta cau sovint damunt d'un traç de llapis.
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 3;
    ctx.strokeText(element.name, x, y + RADI + 13);
    ctx.fillText(element.name, x, y + RADI + 13);
  }
  ctx.restore();
}

// La fletxa entre dos elements. A una imatge rica les fletxes són el que hi diu què afecta a
// què, i **eren una icona que es deixava anar**: una fletxa que no va enlloc no vol dir res.
export function dibuixaFletxa(ctx, origen, desti, { triat = false, etiqueta = '' } = {}) {
  const dx = desti.x - origen.x, dy = desti.y - origen.y;
  const d = Math.hypot(dx, dy) || 1;
  const ux = dx / d, uy = dy / d;
  const a = { x: origen.x + ux * (RADI + 2), y: origen.y + uy * (RADI + 2) };
  const b = { x: desti.x - ux * (RADI + 6), y: desti.y - uy * (RADI + 6) };

  ctx.save();
  const color = triat ? LLENC.estoc.seleccio : SIMBOLS.arrow.traç;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = triat ? GRUIX : GRUIX - 0.6;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();

  const ang = Math.atan2(uy, ux);
  ctx.beginPath();
  ctx.moveTo(b.x, b.y);
  ctx.lineTo(b.x - 12 * Math.cos(ang - 0.4), b.y - 12 * Math.sin(ang - 0.4));
  ctx.lineTo(b.x - 12 * Math.cos(ang + 0.4), b.y - 12 * Math.sin(ang + 0.4));
  ctx.closePath();
  ctx.fill();

  if (etiqueta) {
    ctx.fillStyle = LLENC.text;
    ctx.font = '11px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 3;
    ctx.strokeText(etiqueta, (a.x + b.x) / 2, (a.y + b.y) / 2 - 6);
    ctx.fillText(etiqueta, (a.x + b.x) / 2, (a.y + b.y) / 2 - 6);
  }
  ctx.restore();
}

// Encertar una fletxa: distància del punt al segment.
export function aQuinaFletxa(fletxes, elements, x, y, marge = 8) {
  const cerca = (id) => elements.find((e) => e.id === id);
  for (let i = fletxes.length - 1; i >= 0; i--) {
    const f = fletxes[i];
    const o = cerca(f.from), d = cerca(f.to);
    if (!o || !d) continue;
    const vx = d.x - o.x, vy = d.y - o.y;
    const ll = vx * vx + vy * vy;
    const t = ll === 0 ? 0 : Math.max(0, Math.min(1, ((x - o.x) * vx + (y - o.y) * vy) / ll));
    const px = o.x + t * vx, py = o.y + t * vy;
    if (Math.hypot(x - px, y - py) <= marge) return f;
  }
  return null;
}

const IMATGE = { RADI, GRUIX, TIPUS, dinsDe, aQuinElement, dibuixaSimbol, dibuixaFletxa, aQuinaFletxa };
export default IMATGE;
